import { Link } from "react-router-dom";
import { Instagram, Facebook, MessageCircle, MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { NAV, CLUB, gmailCompose } from "@/data/site";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden bg-cab-deep text-cream"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        {/* CTA row */}
        <div className="flex flex-col gap-8 border-b border-white/10 py-14 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/logo-cab.png" alt="Club Atletico Bologna" loading="lazy" decoding="async" className="h-16 w-16 rounded-lg bg-cream/95 p-1.5 object-contain" />
            <div>
              <p className="font-display text-2xl font-extrabold uppercase tracking-tight">
                Club Atletico Bologna
              </p>
              <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-cream/60">
                CAB · Dal {CLUB.founded}
              </p>
            </div>
          </div>
          <a
            href={gmailCompose("Richiesta informazioni CAB", "Ciao, vorrei ricevere maggiori informazioni sui corsi.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-cta-email"
            className="inline-flex items-center gap-2 self-start rounded-md bg-cream px-6 py-3.5 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5 md:self-auto"
          >
            Scrivici una email
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        {/* Links */}
        <div className="grid gap-10 py-14 md:grid-cols-4">
          <div className="md:col-span-1">
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cream/50">
              Navigazione
            </h4>
            <ul className="mt-5 space-y-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
                    className="text-cream/80 transition-colors hover:text-cream"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-1">
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cream/50">
              Contatti
            </h4>
            <ul className="mt-5 space-y-4 text-cream/80">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cab-light" />
                <span>{CLUB.address}<br />{CLUB.city}</span>
              </li>
              <li>
                <a href={CLUB.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 hover:text-cream">
                  <Phone className="h-4 w-4 shrink-0 text-cab-light" />
                  {CLUB.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CLUB.email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-cream">
                  <Mail className="h-4 w-4 shrink-0 text-cab-light" />
                  <span className="break-all">{CLUB.email}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CLUB.pec}`} data-testid="footer-pec" className="flex items-start gap-3 hover:text-cream">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-cab-light" />
                  <span className="min-w-0 break-all">
                    {CLUB.pec}
                    <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cream/70 align-middle">PEC</span>
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-1">
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cream/50">
              Orari palestra
            </h4>
            <ul className="mt-5 space-y-3 text-cream/80">
              {CLUB.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4">
                  <span>{h.day}</span>
                  <span className="text-cream/60">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-1">
            <h4 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cream/50">
              Seguici
            </h4>
            <div className="mt-5 flex gap-3">
              {[
                { icon: Instagram, href: CLUB.instagram, label: "Instagram", id: "instagram" },
                { icon: Facebook, href: CLUB.facebook, label: "Facebook", id: "facebook" },
                { icon: MessageCircle, href: CLUB.whatsappHref, label: "WhatsApp", id: "whatsapp" },
              ].map(({ icon: Icon, href, label, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/15 text-cream transition-all duration-300 hover:-translate-y-0.5 hover:border-cab-light hover:bg-white/5"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Oversized wordmark */}
      <div className="pointer-events-none select-none overflow-hidden border-t border-white/10">
        <p className="whitespace-nowrap py-6 text-center font-display text-[16vw] font-extrabold uppercase leading-none tracking-tighter text-cream/5">
          CAB 1948
        </p>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-5 py-6 text-xs text-cream/50 md:flex-row md:items-center md:justify-between md:px-8">
          <p>© {new Date().getFullYear()} Club Atletico Bologna ASD. Tutti i diritti riservati.</p>
          <div className="flex items-center gap-4">
            <Link to="/cookie-policy" data-testid="footer-cookie-policy" className="transition-colors hover:text-cream">Cookie Policy</Link>
            <span className="text-cream/30">·</span>
            <p>Bologna, Italia</p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-cab-deep">
        <div className="mx-auto max-w-[1400px] px-5 py-3 text-center text-[11px] text-cream/40 md:px-8">
          Design & Sviluppo del sito:{" "}
          <a
            href="https://maglidesign.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credits-link"
            className="font-medium text-cream/60 underline underline-offset-2 transition-colors hover:text-cream"
          >
            Matteo Magli Digital Design
          </a>
        </div>
      </div>
    </footer>
  );
}
